/* Product photo requests (no order) — team / dedicated Packing Team side: take photos and send them to the partner. */
import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Camera, CheckCircle2, ImagePlus, Loader2, Send, X } from "lucide-react";
import { API, getAuthHeader } from "@/App";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { PhotoLibraryManager } from "@/components/PhotoLibraryManager";

const when = (iso) => (iso ? new Date(iso).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "");

export const AuthImg = ({ src, alt, className, tid }) => {
  const [url, setUrl] = useState("");
  useEffect(() => {
    let u = "";
    axios.get(src, { headers: getAuthHeader(), responseType: "blob" }).then(r => { u = URL.createObjectURL(r.data); setUrl(u); }).catch(() => {});
    return () => { if (u) URL.revokeObjectURL(u); };
  }, [src]);
  return url ? <a href={url} target="_blank" rel="noreferrer"><img src={url} alt={alt} className={className} data-testid={tid} /></a>
    : <div className={`${className} animate-pulse bg-slate-200`} />;
};

const Sender = ({ r, onSent }) => {
  const cam = useRef(null);
  const gal = useRef(null);
  const [files, setFiles] = useState([]);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const add = (e) => {
    const picked = Array.from(e.target.files || []).map(f => ({ f, url: URL.createObjectURL(f) }));
    setFiles(fs => [...fs, ...picked].slice(0, 5)); e.target.value = "";
  };
  const drop = (i) => setFiles(fs => { URL.revokeObjectURL(fs[i].url); return fs.filter((_, j) => j !== i); });
  const send = async () => {
    setBusy(true);
    try {
      const fd = new FormData();
      files.forEach(x => fd.append("files", x.f));
      if (note.trim()) fd.append("note", note.trim());
      await axios.post(`${API}/order-register/packer/photo-requests/${r.id}/send`, fd, { headers: getAuthHeader(), timeout: 120000 });
      toast.success(`Photos sent to ${r.partner_name}`); files.forEach(x => URL.revokeObjectURL(x.url)); setFiles([]); setNote(""); onSent();
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed — please try again"); }
    setBusy(false);
  };
  const tid = `pr-${r.id}`;
  return (
    <div className="space-y-2">
      <input ref={cam} type="file" accept="image/*" capture="environment" className="hidden" onChange={add} data-testid={`${tid}-camera-input`} />
      <input ref={gal} type="file" accept="image/*" multiple className="hidden" onChange={add} data-testid={`${tid}-gallery-input`} />
      {files.length > 0 && <div className="flex flex-wrap gap-1.5">{files.map((x, i) => (
        <div key={x.url} className="relative h-16 w-16 overflow-hidden rounded-lg border">
          <img src={x.url} alt="" className="h-full w-full object-cover" />
          <button type="button" onClick={() => drop(i)} className="absolute right-0.5 top-0.5 rounded-full bg-white p-0.5 shadow" data-testid={`${tid}-remove-${i}`}><X className="h-3 w-3" /></button>
        </div>))}</div>}
      <div className="flex gap-2">
        <button type="button" onClick={() => cam.current?.click()} data-testid={`${tid}-camera`}
          className="flex h-12 flex-1 items-center justify-center gap-2 rounded-xl border-2 border-dashed border-teal-400 bg-teal-50 text-sm font-semibold text-teal-800"><Camera className="h-5 w-5" /> Take photo</button>
        <button type="button" onClick={() => gal.current?.click()} title="Pick from gallery" data-testid={`${tid}-gallery`}
          className="flex h-12 w-12 items-center justify-center rounded-xl border bg-white text-slate-600"><ImagePlus className="h-5 w-5" /></button>
      </div>
      <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note for the partner (optional)" className="h-9 w-full rounded-lg border px-2 text-sm" data-testid={`${tid}-note`} />
      <button type="button" disabled={busy || !files.length} onClick={send} data-testid={`${tid}-send`}
        className="flex h-11 w-full items-center justify-center gap-2 rounded-xl bg-teal-700 text-sm font-semibold text-white disabled:opacity-40">
        {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />} Send {files.length || ""} photo{files.length === 1 ? "" : "s"}</button>
    </div>
  );
};

const ReqCard = ({ r, onSent }) => (
  <div className="space-y-2 rounded-2xl border bg-white p-3 shadow-sm" data-testid={`pr-card-${r.id}`}>
    <div className="flex items-start gap-2">
      <div className="min-w-0"><p className="text-base font-bold leading-tight text-slate-900">{r.product}</p>
        <p className="text-xs text-slate-500">{r.partner_name} ({r.code}) · {when(r.created_at)}</p></div>
      {r.status === "sent" && <span className="ml-auto inline-flex shrink-0 items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-semibold text-emerald-800" data-testid={`pr-sent-${r.id}`}>
        <CheckCircle2 className="h-3.5 w-3.5" /> Sent · {r.sent_by_name}</span>}
    </div>
    {r.fresh_requested && r.status === "open" && <p className="text-[11px] font-semibold text-indigo-700" data-testid={`pr-fresh-${r.id}`}>Partner wants fresh photos (saved ones were not enough)</p>}
    {r.note && <p className="rounded-lg bg-amber-50 px-2 py-1 text-sm text-amber-900">{r.note}</p>}
    {r.files?.length > 0 && <div className="flex flex-wrap gap-1.5">{r.files.map(f => (
      <AuthImg key={f.id} src={`${API}/order-register/packer/photo-request-files/${f.id}`} alt={f.filename} className="h-16 w-16 rounded-lg object-cover" tid={`pr-img-${f.id}`} />))}</div>}
    <Sender r={r} onSent={onSent} />
  </div>
);

export const PhotoRequestsList = ({ onCount }) => {
  const [st, setSt] = useState("open");
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  const stRef = useRef(st);
  stRef.current = st;
  const load = useCallback(async () => {
    const want = stRef.current;
    try {
      const r = (await axios.get(`${API}/order-register/packer/photo-requests`, { headers: getAuthHeader(), params: { status: want } })).data;
      if (want !== stRef.current) return;
      setD(r); onCount?.(r.open);
    } catch (e) { setErr(e.response?.data?.detail || "Could not load photo requests"); }
  }, [onCount]);
  useEffect(() => { load(); }, [load, st]);
  if (err) return <p className="py-8 text-center text-sm text-slate-500" data-testid="pr-error">{err}</p>;
  return (
    <div className="space-y-3" data-testid="photo-requests-list">
      <div className="flex gap-1.5">{[["open", "To do"], ["sent", "Sent"], ["all", "All"]].map(([k, l]) => (
        <button key={k} type="button" onClick={() => setSt(k)} data-testid={`pr-filter-${k}`}
          className={`rounded-full border px-3 py-1 text-xs font-semibold ${st === k ? "border-teal-800 bg-teal-800 text-white" : "bg-white text-slate-600"}`}>
          {l}{k === "open" && d ? ` ${d.open}` : ""}</button>))}</div>
      {!d ? <Loader2 className="mx-auto h-5 w-5 animate-spin text-teal-700" />
        : !d.rows.length ? <p className="py-8 text-center text-sm text-slate-400" data-testid="pr-empty">{st === "open" ? "No product photo requests waiting" : "Nothing here yet"}</p>
          : <div className="grid gap-3 md:grid-cols-2">{d.rows.map(r => <ReqCard key={r.id} r={r} onSent={load} />)}</div>}
    </div>
  );
};

export const PhotoRequestsButton = () => {
  const [open, setOpen] = useState(() => new URLSearchParams(window.location.search).get("photo_requests") === "1");
  const [n, setN] = useState(0);
  const [tab, setTab] = useState("requests");
  const isAdmin = (() => { try { return JSON.parse(localStorage.getItem("user") || "{}").role === "admin"; } catch { return false; } })();
  useEffect(() => {
    const poll = () => axios.get(`${API}/order-register/packer/photo-requests`, { headers: getAuthHeader(), params: { status: "open" } })
      .then(r => setN(r.data.open)).catch(() => {});
    poll(); const t = setInterval(poll, 120000); return () => clearInterval(t);
  }, []);
  return (<>
    <button type="button" onClick={() => setOpen(true)} title="Product photo requests from partners (no order)" data-testid="or-photo-requests-btn"
      className={`inline-flex h-7 items-center gap-1 rounded border px-2 text-[11px] font-semibold ${n ? "border-rose-300 bg-rose-50 text-rose-700" : "text-slate-600"}`}>
      <ImagePlus className="h-3.5 w-3.5" /> Photo requests{n > 0 && <span className="rounded-full bg-rose-600 px-1.5 text-white" data-testid="or-photo-requests-count">{n}</span>}</button>
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-h-[88vh] max-w-3xl overflow-y-auto" data-testid="or-photo-requests-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><ImagePlus className="h-5 w-5 text-teal-700" /> Product photo requests</DialogTitle></DialogHeader>
        {isAdmin && <div className="flex gap-1 border-b pb-2">{[["requests", "Requests"], ["library", "Photo library"]].map(([k, l]) => (
          <button key={k} type="button" onClick={() => setTab(k)} data-testid={`or-pr-tab-${k}`}
            className={`rounded-lg px-3 py-1.5 text-xs font-semibold ${tab === k ? "bg-teal-700 text-white" : "text-slate-600 hover:bg-slate-100"}`}>{l}</button>))}</div>}
        {tab === "library" && isAdmin ? <PhotoLibraryManager /> : <PhotoRequestsList onCount={setN} />}
      </DialogContent>
    </Dialog>
  </>);
};
